import { useState } from "react";
import { ActionsMenu, ActionsMenuButton, MenuItem, ActionMenuDivider } from "ui/actions-menu";

import { useCurrentCampaign } from "hooks/api/use-campaigns";

import { TouchpointType, TouchpointTypeLabel } from "types/touchpoint";

import Icons from "assets";
import { useFeatureFlag } from "contexts/feature-flag-context";

type NewCreativeMenuProps = {
  onCreateFolder?: () => void;
  onAddFromTemplate?: () => void;
  onOpenCreativeDetails?: (type: TouchpointType) => void;
  onAddCreativesWithWriter?: () => void;
} & (
  | { page: "creative" }
  | {
      page: "folder";
      /**
       * Whether the current folder is linked to Monday production items.
       * Used to decide if "From Monday" should be available in folder context.
       */
      isMondayFolder: boolean;
    }
);

const NewCreativeMenu = (props: NewCreativeMenuProps) => {
  const { page, onCreateFolder, onAddFromTemplate, onOpenCreativeDetails, onAddCreativesWithWriter } =
    props;

  const [isNewCreativeDropdownOpen, setIsNewCreativeDropdownOpen] = useState(false);

  const handleNewCreativeDropdownOpen = () => setIsNewCreativeDropdownOpen(true);
  const handleNewCreativeDropdownClose = () => setIsNewCreativeDropdownOpen(false);

  const isProgramBriefsEnabled = useFeatureFlag("program-briefs");
  const currentCampaign = useCurrentCampaign();

  const { EMAIL, LP_TOUCHPOINT, OTHER, FOLDER } = TouchpointTypeLabel;

  const isMondayFolder = page === "folder" ? props.isMondayFolder : false;

  const buttonTextMap = {
    creative: "Create",
    folder: "New creative",
  } as const;

  const campaignHasStrategyData = currentCampaign.strategyData != null;

  const showsFromStrategy = page === "creative" || isMondayFolder;
  const showsWithWriter = isProgramBriefsEnabled && campaignHasStrategyData;
  const showsFolder = page !== "folder";
  const showsSecondaryActions = showsFromStrategy || showsWithWriter || showsFolder;

  return (
    <ActionsMenu
      itemName="create new"
      open={isNewCreativeDropdownOpen}
      onOpen={handleNewCreativeDropdownOpen}
      onClose={handleNewCreativeDropdownClose}
      menuId="new-creative-menu"
      offsetLeft={2}
      offsetTop={10}
      trigger={
        <ActionsMenuButton size="large" data-testid="new-creative-menu-button">
          {buttonTextMap[page]}
        </ActionsMenuButton>
      }>
      <div data-testid="new-creative-menu-content">
        <MenuItem
          icon={<Icons.Email />}
          label={EMAIL}
          onClick={() => {
            onOpenCreativeDetails?.(TouchpointType.EMAIL);
            setIsNewCreativeDropdownOpen(false);
          }}
          data-testid="new-creative-menu-email"
        />
        <MenuItem
          icon={<Icons.LandingPage />}
          label={LP_TOUCHPOINT}
          onClick={() => {
            onOpenCreativeDetails?.(TouchpointType.LANDING_PAGE);
            setIsNewCreativeDropdownOpen(false);
          }}
          data-testid="new-creative-menu-landing-page"
        />
        <MenuItem
          icon={<Icons.Other />}
          label={OTHER}
          onClick={() => {
            onOpenCreativeDetails?.(TouchpointType.OTHER);
            setIsNewCreativeDropdownOpen(false);
          }}
          data-testid="new-creative-menu-other"
        />
        {showsSecondaryActions && <ActionMenuDivider />}
        {showsWithWriter && (
          <MenuItem
            icon={<Icons.HistoryEdu />}
            label="From brief"
            onClick={() => {
              onAddCreativesWithWriter?.();
              setIsNewCreativeDropdownOpen(false);
            }}
            data-testid="new-creative-menu-with-writer"
          />
        )}
        {showsFromStrategy && (
          <MenuItem
            icon={<Icons.Creatives />}
            label="From Monday"
            onClick={() => {
              onAddFromTemplate?.();
              setIsNewCreativeDropdownOpen(false);
            }}
            data-testid="new-creative-menu-from-template"
          />
        )}
        {showsFolder && <ActionMenuDivider />}
        {showsFolder && (
          <MenuItem
            icon={<Icons.Folder />}
            label={FOLDER}
            onClick={() => {
              onCreateFolder?.();
              setIsNewCreativeDropdownOpen(false);
            }}
            data-testid="new-creative-menu-folder"
          />
        )}
      </div>
    </ActionsMenu>
  );
};

export default NewCreativeMenu;
